import packageJSON from '../../package.json'

export const config = {
  AMPLITUDE_CHANNEL: process.env.REACT_APP__AMPLITUDE_CHANNEL as string,
  AMPLITUDE_KEY: process.env.REACT_APP__AMPLITUDE_KEY as string,
  AMPLITUDE_NPS_APP: process.env.REACT_APP__AMPLITUDE_NPS_APP as string,
  AMPLITUDE_NPS_KEY: process.env.REACT_APP__AMPLITUDE_NPS_KEY as string,
  API_KEY: process.env.REACT_APP__HERE_API_KEY as string,
  APP_KEY: process.env.REACT_APP__HERE_APP_KEY as string,
  APP_KEY_FEEDBACK: process.env.REACT_APP__HERE_APP_KEY_FEEDBACK as string,
  APP_VERSION: packageJSON.version,
  FEATURE_FLAGS:
    process.env.REACT_APP__FF_AVAILABLE !== undefined
      ? process.env.REACT_APP__FF_AVAILABLE.split(' ').filter((ff) => ff)
      : [],
  HA_CLIENT_ID: process.env.REACT_APP__HA_CLIENT_ID as string,
  HA_URL: process.env.REACT_APP__HA_URL as string,
  HA_USERINFO_URL: process.env.REACT_APP__HA_USERINFO_URL as string,
  ROOT_PATH: (process.env.REACT_APP__ROOT_PATH as string) || '',
  SCBE_URL: process.env.REACT_APP__SCBE_URL as string,
  SHARE_HERE_COM_URL:
    (process.env.REACT_APP__SHARE_HERE_COM_URL as string) || 'https://share.here.com',
} as const

export const userConfig = {
  countryCode: null,
  defaultPoliticalView: 'international',
  language: 'en-US',
}

export const collectionsConfig = {
  collectionPageSize: 30,
  collectionsRequestDelay: 0,
  placePageSize: 30,
  placesRequestDelay: 10,
}

export const mapConfig = {
  // for request options, see https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions
  geolocation: {
    accuracyThreshold: 500, // chosen empirically to show enough map context around
    accurateZoomLevel: 16,
    defaultPosition: { lat: 52.5, lng: 13.4 }, // Berlin
    defaultZoomLevel: 10,
    defaultZoomLevelPosition: 16,
    recenteringDistanceThreshold: 50, // meters
    requestCacheMaxAge: 60000, // ms
    requestTimeout: 60000, // ms
    requestWithHighAccuracy: true, // when "true", may take longer and drain battery
  },
  layers: {
    defaultLanguage: 'en',
    // see: https://developer.here.com/documentation/map-tile/dev_guide/topics/resource-base-maptile.html
    raster: {
      satellite: {
        // WEGO-10170: Hardcode languages supported by RTS service as workaround. Definitive fix should
        // update this list with what's retrived from endpoint `https://maps.hereapi.com/v3/languages`
        // on each release.
        languages: [
          'ar',
          'as',
          'az',
          'be',
          'bg',
          'bn',
          'bs',
          'ca',
          'cs',
          'cy',
          'da',
          'de',
          'el',
          'en',
          'es',
          'et',
          'eu',
          'fi',
          'fo',
          'fr',
          'ga',
          'gl',
          'gn',
          'gu',
          'he',
          'hi',
          'hr',
          'hu',
          'hy',
          'id',
          'is',
          'it',
          'ja',
          'ka',
          'kk',
          'km',
          'kn',
          'ko',
          'ky',
          'lt',
          'lv',
          'mk',
          'ml',
          'mr',
          'ms',
          'mt',
          'my',
          'nl',
          'no',
          'or',
          'pa',
          'pl',
          'pt',
          'ro',
          'ru',
          'sk',
          'sl',
          'sq',
          'sr',
          'sv',
          'ta',
          'te',
          'th',
          'tr',
          'uk',
          'uz',
          'vi',
          'zh',
          'zh-Hant',
        ],

        name: 'satellite',
        // WEGO-10170: Hardcode political views supported by RTS service as workaround. Definitive fix should
        // update this list with what's retrived from endpoint `https://maps.hereapi.com/v3/politicalViews`
        // on each release.
        politicalViews: [
          'AB',
          'AR',
          'CY',
          'EG',
          'GR',
          'IN',
          'KE',
          'MA',
          'OS',
          'PK',
          'PS',
          'RS',
          'RU',
          'SD',
          'SR',
          'SY',
          'TR',
          'TZ',
          'UY',
        ],
        ppis: {
          highRes: 320,
          lowRes: 72,
        },
        tileFormat: 'jpg',
        tileScheme: 'hybrid.day',
        tileSizes: {
          highRes: 512,
          lowRes: 256,
        },
        tileType: 'maptile',
        type: 'aerial',
      },
    },
    vector: {
      omv: {
        name: 'omv',
        styleBaseUrlTemplate: 'https://js.api.here.com/v3/3.{version}/styles/omv/oslo/',
        styleScheme: 'normal.day.yaml',
      },
    },
  },
  mapsApiVersion: `3.${packageJSON.dependencies['@here/maps-api-for-javascript']}`,
  markers: {
    position: {
      dimensions: {
        height: '52',
        width: '40',
      },
      style: {
        left: '-20px',
        top: '-50px',
      },
    },
    routeDestinationPoint: {
      normal: {
        dimensions: {
          height: '56',
          width: '69',
        },
        style: {
          left: '-34px',
          top: '-62px',
        },
      },
    },
    routeEndPoints: {
      normal: {
        dimensions: {
          height: '20',
          width: '20',
        },
        style: {
          left: '-10px',
          top: '-10px',
        },
      },
    },
  },
  padding: {
    default: {
      mapPanelWidth: 440,
      screenBreakPoint: 1500,
    },
    markerHeight: 42,
    mobile: {
      mapPanelWidth: 376,
      screenBreakpoint: 600,
    },
  },
  services: {
    search: {
      autosuggest: {
        limit: 5,
      },
      discover: {
        limit: 25,
      },
      recent: {
        limit: 5,
      },
    },
  },
  storageRootKey: 'here_web',
  transportModes: ['car', 'rail', 'bicycle', 'pedestrian'],
  url: {
    paramPrecision: {
      coord: 5,
      zoom: 2,
    },
    paramTolerance: {
      coord: 0.000001,
      zoom: 0.001,
    },
    path: {
      directions: 'directions',
      discover: 'discover',
      location: 'location',
      pdc: 'p',
    },
    searchParam: {
      map: 'map',
    },
  },
  zoomAnimation: 3,
}

export const disputedRegionsCountryCodes = [
  {
    iso2: 'ar',
    iso3: 'arg',
  },
  {
    iso2: 'cy',
    iso3: 'cyp',
  },
  {
    iso2: 'eg',
    iso3: 'egy',
  },
  {
    iso2: 'in',
    iso3: 'ind',
  },
  {
    iso2: 'ke',
    iso3: 'ken',
  },
  {
    iso2: 'ma',
    iso3: 'mar',
  },
  {
    iso2: 'pk',
    iso3: 'pak',
  },
  {
    iso2: 'ps',
    iso3: 'pse',
  },
  {
    iso2: 'rs',
    iso3: 'srb',
  },
  {
    iso2: 'ru',
    iso3: 'rus',
  },
  {
    iso2: 'sd',
    iso3: 'sdn',
  },
  {
    iso2: 'sr',
    iso3: 'sur',
  },
  {
    iso2: 'sy',
    iso3: 'syr',
  },
  {
    iso2: 'tz',
    iso3: 'tza',
  },
  {
    iso2: 'uy',
    iso3: 'ury',
  },
  {
    iso2: 'vn',
    iso3: 'vnm',
  },
]
